import React, { useState, useMemo, useEffect } from 'react';
import { Search, X, RotateCcw, Loader2, ChevronDown, ChevronRight } from 'lucide-react';
import { FilterOptions, FilterState } from './types';
import { useAuth } from '../../../../contexts/AuthContext';
import { stcBinSizeService } from '../../../../services/stcBinSizeService';
import { stcBinTypeService, StcBinTypeDto } from '../../../../services/stcBinTypeService';
import { stcBinGrpService, StcBinGrpDto } from '../../../../services/stcBinGrpService';
import { stcBinCategoryService, StcBinCategoryDto } from '../../../../services/stcBinCategoryService';
import { stcZoneService, StcZoneDto } from '../../../../services/stcZoneService';
import { stcPriorityService, StcPriorityDto } from '../../../../services/stcPriorityService';
import { stcResourceService, StcResourceDto } from '../../../../services/stcResourceService';
import { StcBinSizeDto } from '../../../../types/stcBinSize';

interface FilterPanelProps {
    filterOptions: FilterOptions | null;
    filterState: FilterState;
    onFilterChange: (newState: FilterState) => void;
    onReset: () => void;
}

/**
 * Filter panel for filtering locations (FR-103)
 *
 * Features:
 * - Checkboxes for each filter category with search
 * - Radio buttons for boolean properties (PickableYesNo, Reservasjon, etc.)
 * - Number range filters for numeric properties
 * - Live filtering (auto-applies on change)
 * - Reset button to clear all filters
 * - Shows ID + Description for all properties (matching BulkUpdatePanel)
 * - Only shows values from reference tables (not orphaned values from StcBin)
 * - Collapsible groups with active filter counts
 */
export const FilterPanel: React.FC<FilterPanelProps> = ({
    filterOptions,
    filterState,
    onFilterChange,
    onReset
}) => {
    const { user } = useAuth();
    const companyNo = user?.companyNo || '';

    // Collapsible group state - all collapsed by default
    const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({
        binSize: false,
        binType: false,
        resource: false,
        zone: false,
        binGroup: false,
        binCategory: false,
        pickPriority: false,
        putAwayPriority: false,
        properties: false,
        numberRanges: false
    });

    // Search terms for each category
    const [binSizeSearch, setBinSizeSearch] = useState('');
    const [binTypeSearch, setBinTypeSearch] = useState('');
    const [resourceSearch, setResourceSearch] = useState('');
    const [zoneSearch, setZoneSearch] = useState('');
    const [binGrpSearch, setBinGrpSearch] = useState('');
    const [binCatSearch, setBinCatSearch] = useState('');
    const [pickPrioritySearch, setPickPrioritySearch] = useState('');
    const [putAwayPrioritySearch, setPutAwayPrioritySearch] = useState('');

    // Load real data from API for descriptions - THESE ARE THE SOURCE OF TRUTH
    const [binSizes, setBinSizes] = useState<StcBinSizeDto[]>([]);
    const [binTypes, setBinTypes] = useState<StcBinTypeDto[]>([]);
    const [binGroups, setBinGroups] = useState<StcBinGrpDto[]>([]);
    const [binCategories, setBinCategories] = useState<StcBinCategoryDto[]>([]);
    const [zones, setZones] = useState<StcZoneDto[]>([]);
    const [priorities, setPriorities] = useState<StcPriorityDto[]>([]);
    const [resources, setResources] = useState<StcResourceDto[]>([]);

    const [isLoadingBinSizes, setIsLoadingBinSizes] = useState(false);
    const [isLoadingBinTypes, setIsLoadingBinTypes] = useState(false);
    const [isLoadingBinGroups, setIsLoadingBinGroups] = useState(false);
    const [isLoadingBinCategories, setIsLoadingBinCategories] = useState(false);
    const [isLoadingZones, setIsLoadingZones] = useState(false);
    const [isLoadingPriorities, setIsLoadingPriorities] = useState(false);
    const [isLoadingResources, setIsLoadingResources] = useState(false);

    // Load all data on mount
    useEffect(() => {
        const loadData = async () => {
            if (!companyNo) return;

            // Load bin sizes
            setIsLoadingBinSizes(true);
            try {
                const sizes = await stcBinSizeService.getByCompanyNo(companyNo);
                setBinSizes(sizes);
            } catch (err) {
                console.error('Failed to load bin sizes:', err);
            } finally {
                setIsLoadingBinSizes(false);
            }

            // Load bin types
            setIsLoadingBinTypes(true);
            try {
                const types = await stcBinTypeService.getByCompanyNo(companyNo);
                setBinTypes(types);
            } catch (err) {
                console.error('Failed to load bin types:', err);
            } finally {
                setIsLoadingBinTypes(false);
            }

            // Load bin groups
            setIsLoadingBinGroups(true);
            try {
                const groups = await stcBinGrpService.getByCompanyNo(companyNo);
                setBinGroups(groups);
            } catch (err) {
                console.error('Failed to load bin groups:', err);
            } finally {
                setIsLoadingBinGroups(false);
            }

            // Load bin categories
            setIsLoadingBinCategories(true);
            try {
                const categories = await stcBinCategoryService.getByCompanyNo(companyNo);
                setBinCategories(categories);
            } catch (err) {
                console.error('Failed to load bin categories:', err);
            } finally {
                setIsLoadingBinCategories(false);
            }

            // Load zones
            setIsLoadingZones(true);
            try {
                const zoneList = await stcZoneService.getByCompanyNo(companyNo);
                setZones(zoneList);
            } catch (err) {
                console.error('Failed to load zones:', err);
            } finally {
                setIsLoadingZones(false);
            }

            // Load priorities
            setIsLoadingPriorities(true);
            try {
                const priorityList = await stcPriorityService.getByCompanyNo(companyNo);
                setPriorities(priorityList);
            } catch (err) {
                console.error('Failed to load priorities:', err);
            } finally {
                setIsLoadingPriorities(false);
            }

            // Load resources
            setIsLoadingResources(true);
            try {
                const resourceList = await stcResourceService.getByCompanyNo(companyNo);
                setResources(resourceList);
            } catch (err) {
                console.error('Failed to load resources:', err);
            } finally {
                setIsLoadingResources(false);
            }
        };

        loadData();
    }, [companyNo]);

    /**
     * Toggle group expansion
     */
    const toggleGroup = (groupKey: string) => {
        setExpandedGroups(prev => ({
            ...prev,
            [groupKey]: !prev[groupKey]
        }));
    };

    /**
     * Count active filters for a group
     */
    const getActiveFilterCount = (groupKey: string): number => {
        switch (groupKey) {
            case 'binSize':
                return filterState.binSizeNo.length;
            case 'binType':
                return filterState.binTypeNo.length;
            case 'resource':
                return filterState.resourceNo.length;
            case 'zone':
                return filterState.stcZoneID.length;
            case 'binGroup':
                return filterState.binGrpNo.length;
            case 'binCategory':
                return filterState.stcBinCatNo.length;
            case 'pickPriority':
                return filterState.pickPriorityNo.length;
            case 'putAwayPriority':
                return filterState.putAwayPriorityNo.length;
            case 'properties':
                let propCount = 0;
                if (filterState.pickableYesNo !== 'all') propCount++;
                if (filterState.itemNo !== 'all') propCount++;
                if (filterState.reservationAllowed !== 'all') propCount++;
                if (filterState.autoReplenishment !== 'all') propCount++;
                if (filterState.negativeReservationAllowed !== 'all') propCount++;
                if (filterState.sharedBinYesNo !== 'all') propCount++;
                if (filterState.sharedBinPreferEmptyBin !== 'all') propCount++;
                return propCount;
            case 'numberRanges':
                let rangeCount = 0;
                if (filterState.replenishMinLevelFrom > 0 || filterState.replenishMinLevelTo < 9999) rangeCount++;
                if (filterState.replenishMaxLevelFrom > 0 || filterState.replenishMaxLevelTo < 9999) rangeCount++;
                if (filterState.customSortIDFrom > 0 || filterState.customSortIDTo < 9999) rangeCount++;
                if (filterState.sharedBinMaxQuantityFrom > 0 || filterState.sharedBinMaxQuantityTo < 9999) rangeCount++;
                return rangeCount;
            default:
                return 0;
        }
    };

    /**
     * Get bin size description for display
     */
    const getBinSizeDescription = (sizeNo: number): string => {
        const size = binSizes.find(s => s.binSizeNo === sizeNo);
        return size ? `${sizeNo} - ${size.description1}` : `${sizeNo}`;
    };

    /**
     * Get bin type description for display
     */
    const getBinTypeDescription = (typeNo: number): string => {
        const type = binTypes.find(t => t.binTypeNo === typeNo);
        return type ? `${typeNo} - ${type.description1 || 'Bin Type'}` : `${typeNo}`;
    };

    /**
     * Get bin group description for display
     */
    const getBinGrpDescription = (grpNo: number): string => {
        const group = binGroups.find(g => g.binGrpNo === grpNo);
        return group ? `${grpNo} - ${group.description1}` : `${grpNo}`;
    };

    /**
     * Get bin category description for display
     */
    const getBinCategoryDescription = (catNo: number): string => {
        const category = binCategories.find(c => c.stcBinCatNo === catNo);
        return category ? `${catNo} - ${category.lng_1}` : `${catNo}`;
    };

    /**
     * Get zone description for display
     */
    const getZoneDescription = (zoneID: string): string => {
        const zone = zones.find(z => z.stcZoneID === zoneID);
        return zone ? `${zoneID} - ${zone.description1}` : zoneID;
    };

    /**
     * Get priority description for display
     */
    const getPriorityDescription = (priorityNo: number): string => {
        const priority = priorities.find(p => p.priorityNo === priorityNo);
        return priority ? `${priorityNo} - ${priority.description1}` : `${priorityNo}`;
    };

    /**
     * Get resource description for display
     */
    const getResourceDescription = (resourceNo: number): string => {
        const resource = resources.find(r => r.resourceNo === resourceNo);
        return resource ? `${resourceNo} - ${resource.description1}` : `${resourceNo}`;
    };

    /**
     * Filter values based on search term - searches both ID and description
     */
    const filterValues = <T extends number | string>(
        values: T[],
        searchTerm: string,
        getDescription: (value: T) => string
    ): T[] => {
        if (!searchTerm) return values;
        const lower = searchTerm.toLowerCase();
        return values.filter(v => getDescription(v).toLowerCase().includes(lower));
    };

    /**
     * Toggle a value in array
     */
    const toggleValue = <T,>(array: T[], value: T): T[] => {
        if (array.includes(value)) {
            return array.filter(v => v !== value);
        }
        return [...array, value];
    };

    /**
     * Handle checkbox change for numeric arrays
     */
    const handleNumericCheckbox = (
        field: 'binSizeNo' | 'binTypeNo' | 'resourceNo' | 'binGrpNo' | 'stcBinCatNo' | 'pickPriorityNo' | 'putAwayPriorityNo',
        value: number
    ) => {
        onFilterChange({
            ...filterState,
            [field]: toggleValue(filterState[field], value)
        });
    };

    /**
     * Handle checkbox change for string arrays
     */
    const handleStringCheckbox = (
        field: 'stcZoneID',
        value: string
    ) => {
        onFilterChange({
            ...filterState,
            [field]: toggleValue(filterState[field], value)
        });
    };

    /**
     * Handle radio button change
     */
    const handleRadioChange = (
        field: 'pickableYesNo' | 'itemNo' | 'reservationAllowed' | 'autoReplenishment' | 'negativeReservationAllowed' | 'sharedBinYesNo' | 'sharedBinPreferEmptyBin',
        value: 'all' | 'yes' | 'no' | 'has' | 'empty'
    ) => {
        onFilterChange({
            ...filterState,
            [field]: value as any
        });
    };

    /**
     * Handle number range change
     */
    const handleNumberRangeChange = (
        field: 'replenishMinLevelFrom' | 'replenishMinLevelTo' | 'replenishMaxLevelFrom' | 'replenishMaxLevelTo' |
              'customSortIDFrom' | 'customSortIDTo' | 'sharedBinMaxQuantityFrom' | 'sharedBinMaxQuantityTo',
        value: number
    ) => {
        onFilterChange({
            ...filterState,
            [field]: value
        });
    };

    // Use data from API instead of filterOptions - this ensures we only show values from reference tables
    const availableBinSizeNo = useMemo(
        () => binSizes.map(s => s.binSizeNo),
        [binSizes]
    );

    const availableBinTypeNo = useMemo(
        () => binTypes.map(t => t.binTypeNo),
        [binTypes]
    );

    const availableResourceNo = useMemo(
        () => resources.map(r => r.resourceNo),
        [resources]
    );

    const availableStcZoneID = useMemo(
        () => zones.map(z => z.stcZoneID),
        [zones]
    );

    const availableBinGrpNo = useMemo(
        () => binGroups.map(g => g.binGrpNo),
        [binGroups]
    );

    const availableStcBinCatNo = useMemo(
        () => binCategories.map(c => c.stcBinCatNo),
        [binCategories]
    );

    const availablePickPriorityNo = useMemo(
        () => priorities.map(p => p.priorityNo),
        [priorities]
    );

    const availablePutAwayPriorityNo = useMemo(
        () => priorities.map(p => p.priorityNo),
        [priorities]
    );

    // Filtered values for each category with descriptions
    const filteredBinSizeNo = useMemo(
        () => filterValues(availableBinSizeNo, binSizeSearch, getBinSizeDescription),
        [availableBinSizeNo, binSizeSearch, binSizes]
    );

    const filteredBinTypeNo = useMemo(
        () => filterValues(availableBinTypeNo, binTypeSearch, getBinTypeDescription),
        [availableBinTypeNo, binTypeSearch, binTypes]
    );

    const filteredResourceNo = useMemo(
        () => filterValues(availableResourceNo, resourceSearch, getResourceDescription),
        [availableResourceNo, resourceSearch, resources]
    );

    const filteredStcZoneID = useMemo(
        () => filterValues(availableStcZoneID, zoneSearch, getZoneDescription),
        [availableStcZoneID, zoneSearch, zones]
    );

    const filteredBinGrpNo = useMemo(
        () => filterValues(availableBinGrpNo, binGrpSearch, getBinGrpDescription),
        [availableBinGrpNo, binGrpSearch, binGroups]
    );

    const filteredStcBinCatNo = useMemo(
        () => filterValues(availableStcBinCatNo, binCatSearch, getBinCategoryDescription),
        [availableStcBinCatNo, binCatSearch, binCategories]
    );

    const filteredPickPriorityNo = useMemo(
        () => filterValues(availablePickPriorityNo, pickPrioritySearch, getPriorityDescription),
        [availablePickPriorityNo, pickPrioritySearch, priorities]
    );

    const filteredPutAwayPriorityNo = useMemo(
        () => filterValues(availablePutAwayPriorityNo, putAwayPrioritySearch, getPriorityDescription),
        [availablePutAwayPriorityNo, putAwayPrioritySearch, priorities]
    );

    /**
     * Check if any filters are active
     */
    const hasActiveFilters = (): boolean => {
        return (
            filterState.binSizeNo.length > 0 ||
            filterState.binTypeNo.length > 0 ||
            filterState.resourceNo.length > 0 ||
            filterState.stcZoneID.length > 0 ||
            filterState.binGrpNo.length > 0 ||
            filterState.stcBinCatNo.length > 0 ||
            filterState.pickPriorityNo.length > 0 ||
            filterState.putAwayPriorityNo.length > 0 ||
            filterState.pickableYesNo !== 'all' ||
            filterState.itemNo !== 'all' ||
            filterState.reservationAllowed !== 'all' ||
            filterState.autoReplenishment !== 'all' ||
            filterState.negativeReservationAllowed !== 'all' ||
            filterState.sharedBinYesNo !== 'all' ||
            filterState.sharedBinPreferEmptyBin !== 'all' ||
            filterState.replenishMinLevelFrom > 0 ||
            filterState.replenishMinLevelTo < 9999 ||
            filterState.replenishMaxLevelFrom > 0 ||
            filterState.replenishMaxLevelTo < 9999 ||
            filterState.customSortIDFrom > 0 ||
            filterState.customSortIDTo < 9999 ||
            filterState.sharedBinMaxQuantityFrom > 0 ||
            filterState.sharedBinMaxQuantityTo < 9999
        );
    };

    const isLoading = isLoadingBinSizes || isLoadingBinTypes || isLoadingBinGroups ||
                      isLoadingBinCategories || isLoadingZones || isLoadingPriorities || isLoadingResources;

    /**
     * Collapsible group header component
     */
    const GroupHeader: React.FC<{ groupKey: string; title: string }> = ({ groupKey, title }) => {
        const isExpanded = expandedGroups[groupKey];
        const activeCount = getActiveFilterCount(groupKey);

        return (
            <button
                onClick={() => toggleGroup(groupKey)}
                className="w-full flex items-center justify-between p-2 hover:bg-gray-50 rounded transition-colors"
            >
                <div className="flex items-center space-x-2">
                    {isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-gray-500" />
                    ) : (
                        <ChevronRight className="w-4 h-4 text-gray-500" />
                    )}
                    <p className="text-xs font-semibold text-gray-700 uppercase">{title}</p>
                </div>
                {activeCount > 0 && (
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                        {activeCount}
                    </span>
                )}
            </button>
        );
    };

    return (
        <div className="space-y-4">
            {/* Reset button */}
            <button
                onClick={onReset}
                disabled={!hasActiveFilters()}
                className="w-full flex items-center justify-center space-x-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
                <RotateCcw className="w-4 h-4" />
                <span>Nullstill filtre</span>
            </button>

            {isLoading && (
                <div className="flex items-center justify-center py-2">
                    <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                    <span className="ml-2 text-xs text-gray-600">Laster beskrivelser...</span>
                </div>
            )}

            {/* BinSizeNo */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
                <GroupHeader groupKey="binSize" title="Bin Storrelse" />
                {expandedGroups.binSize && (
                    <div className="p-3 space-y-2 border-t border-gray-200">
                        {availableBinSizeNo.length > 5 && (
                            <div className="relative">
                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
                                <input
                                    type="text"
                                    value={binSizeSearch}
                                    onChange={(e) => setBinSizeSearch(e.target.value)}
                                    placeholder="Sok..."
                                    className="w-full pl-7 pr-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                />
                                {binSizeSearch && (
                                    <button
                                        onClick={() => setBinSizeSearch('')}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                )}
                            </div>
                        )}
                        <div className="max-h-32 overflow-y-auto space-y-1">
                            {filteredBinSizeNo.length === 0 ? (
                                <p className="text-xs text-gray-500 italic">Ingen treff</p>
                            ) : (
                                filteredBinSizeNo.map(value => (
                                    <label key={value} className="flex items-center space-x-2 text-sm">
                                        <input
                                            type="checkbox"
                                            checked={filterState.binSizeNo.includes(value)}
                                            onChange={() => handleNumericCheckbox('binSizeNo', value)}
                                            className="w-3 h-3 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-gray-700">{getBinSizeDescription(value)}</span>
                                    </label>
                                ))
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* BinTypeNo */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
                <GroupHeader groupKey="binType" title="Bin Type" />
                {expandedGroups.binType && (
                    <div className="p-3 space-y-2 border-t border-gray-200">
                        {availableBinTypeNo.length > 5 && (
                            <div className="relative">
                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
                                <input
                                    type="text"
                                    value={binTypeSearch}
                                    onChange={(e) => setBinTypeSearch(e.target.value)}
                                    placeholder="Sok..."
                                    className="w-full pl-7 pr-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                />
                                {binTypeSearch && (
                                    <button
                                        onClick={() => setBinTypeSearch('')}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                )}
                            </div>
                        )}
                        <div className="max-h-32 overflow-y-auto space-y-1">
                            {filteredBinTypeNo.length === 0 ? (
                                <p className="text-xs text-gray-500 italic">Ingen treff</p>
                            ) : (
                                filteredBinTypeNo.map(value => (
                                    <label key={value} className="flex items-center space-x-2 text-sm">
                                        <input
                                            type="checkbox"
                                            checked={filterState.binTypeNo.includes(value)}
                                            onChange={() => handleNumericCheckbox('binTypeNo', value)}
                                            className="w-3 h-3 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-gray-700">{getBinTypeDescription(value)}</span>
                                    </label>
                                ))
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* ResourceNo */}
            {availableResourceNo.length > 0 && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <GroupHeader groupKey="resource" title="Ressurs" />
                    {expandedGroups.resource && (
                        <div className="p-3 space-y-2 border-t border-gray-200">
                            {availableResourceNo.length > 5 && (
                                <div className="relative">
                                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
                                    <input
                                        type="text"
                                        value={resourceSearch}
                                        onChange={(e) => setResourceSearch(e.target.value)}
                                        placeholder="Sok..."
                                        className="w-full pl-7 pr-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                    {resourceSearch && (
                                        <button
                                            onClick={() => setResourceSearch('')}
                                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    )}
                                </div>
                            )}
                            <div className="max-h-32 overflow-y-auto space-y-1">
                                {isLoadingResources ? (
                                    <p className="text-xs text-gray-500 italic">Laster...</p>
                                ) : filteredResourceNo.length === 0 ? (
                                    <p className="text-xs text-gray-500 italic">Ingen treff</p>
                                ) : (
                                    filteredResourceNo.map(value => (
                                        <label key={value} className="flex items-center space-x-2 text-sm">
                                            <input
                                                type="checkbox"
                                                checked={filterState.resourceNo.includes(value)}
                                                onChange={() => handleNumericCheckbox('resourceNo', value)}
                                                className="w-3 h-3 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                            />
                                            <span className="text-gray-700">{getResourceDescription(value)}</span>
                                        </label>
                                    ))
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* StcZoneID */}
            {availableStcZoneID.length > 0 && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <GroupHeader groupKey="zone" title="Sone" />
                    {expandedGroups.zone && (
                        <div className="p-3 space-y-2 border-t border-gray-200">
                            {availableStcZoneID.length > 5 && (
                                <div className="relative">
                                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
                                    <input
                                        type="text"
                                        value={zoneSearch}
                                        onChange={(e) => setZoneSearch(e.target.value)}
                                        placeholder="Sok..."
                                        className="w-full pl-7 pr-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                    {zoneSearch && (
                                        <button
                                            onClick={() => setZoneSearch('')}
                                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    )}
                                </div>
                            )}
                            <div className="max-h-32 overflow-y-auto space-y-1">
                                {filteredStcZoneID.length === 0 ? (
                                    <p className="text-xs text-gray-500 italic">Ingen treff</p>
                                ) : (
                                    filteredStcZoneID.map(value => (
                                        <label key={value} className="flex items-center space-x-2 text-sm">
                                            <input
                                                type="checkbox"
                                                checked={filterState.stcZoneID.includes(value)}
                                                onChange={() => handleStringCheckbox('stcZoneID', value)}
                                                className="w-3 h-3 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                            />
                                            <span className="text-gray-700">{getZoneDescription(value)}</span>
                                        </label>
                                    ))
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* BinGrpNo */}
            {availableBinGrpNo.length > 0 && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <GroupHeader groupKey="binGroup" title="Bin Gruppe" />
                    {expandedGroups.binGroup && (
                        <div className="p-3 space-y-2 border-t border-gray-200">
                            {availableBinGrpNo.length > 5 && (
                                <div className="relative">
                                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
                                    <input
                                        type="text"
                                        value={binGrpSearch}
                                        onChange={(e) => setBinGrpSearch(e.target.value)}
                                        placeholder="Sok..."
                                        className="w-full pl-7 pr-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                    {binGrpSearch && (
                                        <button
                                            onClick={() => setBinGrpSearch('')}
                                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    )}
                                </div>
                            )}
                            <div className="max-h-32 overflow-y-auto space-y-1">
                                {filteredBinGrpNo.length === 0 ? (
                                    <p className="text-xs text-gray-500 italic">Ingen treff</p>
                                ) : (
                                    filteredBinGrpNo.map(value => (
                                        <label key={value} className="flex items-center space-x-2 text-sm">
                                            <input
                                                type="checkbox"
                                                checked={filterState.binGrpNo.includes(value)}
                                                onChange={() => handleNumericCheckbox('binGrpNo', value)}
                                                className="w-3 h-3 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                            />
                                            <span className="text-gray-700">{getBinGrpDescription(value)}</span>
                                        </label>
                                    ))
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* StcBinCatNo */}
            {availableStcBinCatNo.length > 0 && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <GroupHeader groupKey="binCategory" title="Lokasjonskategori" />
                    {expandedGroups.binCategory && (
                        <div className="p-3 space-y-2 border-t border-gray-200">
                            {availableStcBinCatNo.length > 5 && (
                                <div className="relative">
                                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
                                    <input
                                        type="text"
                                        value={binCatSearch}
                                        onChange={(e) => setBinCatSearch(e.target.value)}
                                        placeholder="Sok..."
                                        className="w-full pl-7 pr-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                    {binCatSearch && (
                                        <button
                                            onClick={() => setBinCatSearch('')}
                                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    )}
                                </div>
                            )}
                            <div className="max-h-32 overflow-y-auto space-y-1">
                                {filteredStcBinCatNo.length === 0 ? (
                                    <p className="text-xs text-gray-500 italic">Ingen treff</p>
                                ) : (
                                    filteredStcBinCatNo.map(value => (
                                        <label key={value} className="flex items-center space-x-2 text-sm">
                                            <input
                                                type="checkbox"
                                                checked={filterState.stcBinCatNo.includes(value)}
                                                onChange={() => handleNumericCheckbox('stcBinCatNo', value)}
                                                className="w-3 h-3 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                            />
                                            <span className="text-gray-700">{getBinCategoryDescription(value)}</span>
                                        </label>
                                    ))
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* PickPriorityNo */}
            {availablePickPriorityNo.length > 0 && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <GroupHeader groupKey="pickPriority" title="Plukk Prioritet" />
                    {expandedGroups.pickPriority && (
                        <div className="p-3 space-y-2 border-t border-gray-200">
                            {availablePickPriorityNo.length > 5 && (
                                <div className="relative">
                                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
                                    <input
                                        type="text"
                                        value={pickPrioritySearch}
                                        onChange={(e) => setPickPrioritySearch(e.target.value)}
                                        placeholder="Sok..."
                                        className="w-full pl-7 pr-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                    {pickPrioritySearch && (
                                        <button
                                            onClick={() => setPickPrioritySearch('')}
                                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    )}
                                </div>
                            )}
                            <div className="max-h-32 overflow-y-auto space-y-1">
                                {filteredPickPriorityNo.length === 0 ? (
                                    <p className="text-xs text-gray-500 italic">Ingen treff</p>
                                ) : (
                                    filteredPickPriorityNo.map(value => (
                                        <label key={value} className="flex items-center space-x-2 text-sm">
                                            <input
                                                type="checkbox"
                                                checked={filterState.pickPriorityNo.includes(value)}
                                                onChange={() => handleNumericCheckbox('pickPriorityNo', value)}
                                                className="w-3 h-3 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                            />
                                            <span className="text-gray-700">{getPriorityDescription(value)}</span>
                                        </label>
                                    ))
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* PutAwayPriorityNo */}
            {availablePutAwayPriorityNo.length > 0 && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <GroupHeader groupKey="putAwayPriority" title="Put-away Prioritet" />
                    {expandedGroups.putAwayPriority && (
                        <div className="p-3 space-y-2 border-t border-gray-200">
                            {availablePutAwayPriorityNo.length > 5 && (
                                <div className="relative">
                                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
                                    <input
                                        type="text"
                                        value={putAwayPrioritySearch}
                                        onChange={(e) => setPutAwayPrioritySearch(e.target.value)}
                                        placeholder="Sok..."
                                        className="w-full pl-7 pr-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                    {putAwayPrioritySearch && (
                                        <button
                                            onClick={() => setPutAwayPrioritySearch('')}
                                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    )}
                                </div>
                            )}
                            <div className="max-h-32 overflow-y-auto space-y-1">
                                {filteredPutAwayPriorityNo.length === 0 ? (
                                    <p className="text-xs text-gray-500 italic">Ingen treff</p>
                                ) : (
                                    filteredPutAwayPriorityNo.map(value => (
                                        <label key={value} className="flex items-center space-x-2 text-sm">
                                            <input
                                                type="checkbox"
                                                checked={filterState.putAwayPriorityNo.includes(value)}
                                                onChange={() => handleNumericCheckbox('putAwayPriorityNo', value)}
                                                className="w-3 h-3 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                            />
                                            <span className="text-gray-700">{getPriorityDescription(value)}</span>
                                        </label>
                                    ))
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Boolean properties as radio buttons */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
                <GroupHeader groupKey="properties" title="Egenskaper" />
                {expandedGroups.properties && (
                    <div className="p-3 space-y-3 border-t border-gray-200">
                        {/* PickableYesNo */}
                        <div className="space-y-1">
                            <p className="text-xs font-medium text-gray-700">Plukkbar</p>
                            <div className="space-y-1">
                                <label className="flex items-center space-x-2 text-sm">
                                    <input
                                        type="radio"
                                        checked={filterState.pickableYesNo === 'all'}
                                        onChange={() => handleRadioChange('pickableYesNo', 'all')}
                                        className="w-3 h-3 text-blue-600 border-gray-300 focus:ring-blue-500"
                                    />
                                    <span className="text-gray-700">Alle</span>
                                </label>
                                <label className="flex items-center space-x-2 text-sm">
                                    <input
                                        type="radio"
                                        checked={filterState.pickableYesNo === 'yes'}
                                        onChange={() => handleRadioChange('pickableYesNo', 'yes')}
                                        className="w-3 h-3 text-blue-600 border-gray-300 focus:ring-blue-500"
                                    />
                                    <span className="text-gray-700">Ja</span>
                                </label>
                                <label className="flex items-center space-x-2 text-sm">
                                    <input
                                        type="radio"
                                        checked={filterState.pickableYesNo === 'no'}
                                        onChange={() => handleRadioChange('pickableYesNo', 'no')}
                                        className="w-3 h-3 text-blue-600 border-gray-300 focus:ring-blue-500"
                                    />
                                    <span className="text-gray-700">Nei</span>
                                </label>
                            </div>
                        </div>

                        {/* ReservationAllowed */}
                        <div className="space-y-1">
                            <p className="text-xs font-medium text-gray-700">Reservasjon</p>
                            <div className="space-y-1">
                                <label className="flex items-center space-x-2 text-sm">
                                    <input
                                        type="radio"
                                        checked={filterState.reservationAllowed === 'all'}
                                        onChange={() => handleRadioChange('reservationAllowed', 'all')}
                                        className="w-3 h-3 text-blue-600 border-gray-300 focus:ring-blue-500"
                                    />
                                    <span className="text-gray-700">Alle</span>
                                </label>
                                <label className="flex items-center space-x-2 text-sm">
                                    <input
                                        type="radio"
                                        checked={filterState.reservationAllowed === 'yes'}
                                        onChange={() => handleRadioChange('reservationAllowed', 'yes')}
                                        className="w-3 h-3 text-blue-600 border-gray-300 focus:ring-blue-500"
                                    />
                                    <span className="text-gray-700">Ja</span>
                                </label>
                                <label className="flex items-center space-x-2 text-sm">
                                    <input
                                        type="radio"
                                        checked={filterState.reservationAllowed === 'no'}
                                        onChange={() => handleRadioChange('reservationAllowed', 'no')}
                                        className="w-3 h-3 text-blue-600 border-gray-300 focus:ring-blue-500"
                                    />
                                    <span className="text-gray-700">Nei</span>
                                </label>
                            </div>
                        </div>

                        {/* AutoReplenishment */}
                        <div className="space-y-1">
                            <p className="text-xs font-medium text-gray-700">Auto-pafylling</p>
                            <div className="space-y-1">
                                <label className="flex items-center space-x-2 text-sm">
                                    <input
                                        type="radio"
                                        checked={filterState.autoReplenishment === 'all'}
                                        onChange={() => handleRadioChange('autoReplenishment', 'all')}
                                        className="w-3 h-3 text-blue-600 border-gray-300 focus:ring-blue-500"
                                    />
                                    <span className="text-gray-700">Alle</span>
                                </label>
                                <label className="flex items-center space-x-2 text-sm">
                                    <input
                                        type="radio"
                                        checked={filterState.autoReplenishment === 'yes'}
                                        onChange={() => handleRadioChange('autoReplenishment', 'yes')}
                                        className="w-3 h-3 text-blue-600 border-gray-300 focus:ring-blue-500"
                                    />
                                    <span className="text-gray-700">Ja</span>
                                </label>
                                <label className="flex items-center space-x-2 text-sm">
                                    <input
                                        type="radio"
                                        checked={filterState.autoReplenishment === 'no'}
                                        onChange={() => handleRadioChange('autoReplenishment', 'no')}
                                        className="w-3 h-3 text-blue-600 border-gray-300 focus:ring-blue-500"
                                    />
                                    <span className="text-gray-700">Nei</span>
                                </label>
                            </div>
                        </div>

                        {/* NegativeReservationAllowed */}
                        <div className="space-y-1">
                            <p className="text-xs font-medium text-gray-700">Negativ reservasjon</p>
                            <div className="space-y-1">
                                <label className="flex items-center space-x-2 text-sm">
                                    <input
                                        type="radio"
                                        checked={filterState.negativeReservationAllowed === 'all'}
                                        onChange={() => handleRadioChange('negativeReservationAllowed', 'all')}
                                        className="w-3 h-3 text-blue-600 border-gray-300 focus:ring-blue-500"
                                    />
                                    <span className="text-gray-700">Alle</span>
                                </label>
                                <label className="flex items-center space-x-2 text-sm">
                                    <input
                                        type="radio"
                                        checked={filterState.negativeReservationAllowed === 'yes'}
                                        onChange={() => handleRadioChange('negativeReservationAllowed', 'yes')}
                                        className="w-3 h-3 text-blue-600 border-gray-300 focus:ring-blue-500"
                                    />
                                    <span className="text-gray-700">Ja</span>
                                </label>
                                <label className="flex items-center space-x-2 text-sm">
                                    <input
                                        type="radio"
                                        checked={filterState.negativeReservationAllowed === 'no'}
                                        onChange={() => handleRadioChange('negativeReservationAllowed', 'no')}
                                        className="w-3 h-3 text-blue-600 border-gray-300 focus:ring-blue-500"
                                    />
                                    <span className="text-gray-700">Nei</span>
                                </label>
                            </div>
                        </div>

                        {/* SharedBinYesNo */}
                        <div className="space-y-1">
                            <p className="text-xs font-medium text-gray-700">Delt lokasjon</p>
                            <div className="space-y-1">
                                <label className="flex items-center space-x-2 text-sm">
                                    <input
                                        type="radio"
                                        checked={filterState.sharedBinYesNo === 'all'}
                                        onChange={() => handleRadioChange('sharedBinYesNo', 'all')}
                                        className="w-3 h-3 text-blue-600 border-gray-300 focus:ring-blue-500"
                                    />
                                    <span className="text-gray-700">Alle</span>
                                </label>
                                <label className="flex items-center space-x-2 text-sm">
                                    <input
                                        type="radio"
                                        checked={filterState.sharedBinYesNo === 'yes'}
                                        onChange={() => handleRadioChange('sharedBinYesNo', 'yes')}
                                        className="w-3 h-3 text-blue-600 border-gray-300 focus:ring-blue-500"
                                    />
                                    <span className="text-gray-700">Ja</span>
                                </label>
                                <label className="flex items-center space-x-2 text-sm">
                                    <input
                                        type="radio"
                                        checked={filterState.sharedBinYesNo === 'no'}
                                        onChange={() => handleRadioChange('sharedBinYesNo', 'no')}
                                        className="w-3 h-3 text-blue-600 border-gray-300 focus:ring-blue-500"
                                    />
                                    <span className="text-gray-700">Nei</span>
                                </label>
                            </div>
                        </div>

                        {/* SharedBinPreferEmptyBin */}
                        <div className="space-y-1">
                            <p className="text-xs font-medium text-gray-700">Foretrekk tom lokasjon</p>
                            <div className="space-y-1">
                                <label className="flex items-center space-x-2 text-sm">
                                    <input
                                        type="radio"
                                        checked={filterState.sharedBinPreferEmptyBin === 'all'}
                                        onChange={() => handleRadioChange('sharedBinPreferEmptyBin', 'all')}
                                        className="w-3 h-3 text-blue-600 border-gray-300 focus:ring-blue-500"
                                    />
                                    <span className="text-gray-700">Alle</span>
                                </label>
                                <label className="flex items-center space-x-2 text-sm">
                                    <input
                                        type="radio"
                                        checked={filterState.sharedBinPreferEmptyBin === 'yes'}
                                        onChange={() => handleRadioChange('sharedBinPreferEmptyBin', 'yes')}
                                        className="w-3 h-3 text-blue-600 border-gray-300 focus:ring-blue-500"
                                    />
                                    <span className="text-gray-700">Ja</span>
                                </label>
                                <label className="flex items-center space-x-2 text-sm">
                                    <input
                                        type="radio"
                                        checked={filterState.sharedBinPreferEmptyBin === 'no'}
                                        onChange={() => handleRadioChange('sharedBinPreferEmptyBin', 'no')}
                                        className="w-3 h-3 text-blue-600 border-gray-300 focus:ring-blue-500"
                                    />
                                    <span className="text-gray-700">Nei</span>
                                </label>
                            </div>
                        </div>

                        {/* ItemNo */}
                        <div className="space-y-1 pt-2 border-t border-gray-200">
                            <p className="text-xs font-medium text-gray-700">Vare</p>
                            <div className="space-y-1">
                                <label className="flex items-center space-x-2 text-sm">
                                    <input
                                        type="radio"
                                        checked={filterState.itemNo === 'all'}
                                        onChange={() => handleRadioChange('itemNo', 'all')}
                                        className="w-3 h-3 text-blue-600 border-gray-300 focus:ring-blue-500"
                                    />
                                    <span className="text-gray-700">Alle</span>
                                </label>
                                <label className="flex items-center space-x-2 text-sm">
                                    <input
                                        type="radio"
                                        checked={filterState.itemNo === 'has'}
                                        onChange={() => handleRadioChange('itemNo', 'has')}
                                        className="w-3 h-3 text-blue-600 border-gray-300 focus:ring-blue-500"
                                    />
                                    <span className="text-gray-700">Har vare</span>
                                </label>
                                <label className="flex items-center space-x-2 text-sm">
                                    <input
                                        type="radio"
                                        checked={filterState.itemNo === 'empty'}
                                        onChange={() => handleRadioChange('itemNo', 'empty')}
                                        className="w-3 h-3 text-blue-600 border-gray-300 focus:ring-blue-500"
                                    />
                                    <span className="text-gray-700">Tom</span>
                                </label>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Number range filters */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
                <GroupHeader groupKey="numberRanges" title="Tallverdier" />
                {expandedGroups.numberRanges && (
                    <div className="p-3 space-y-3 border-t border-gray-200">
                        {/* ReplenishMinLevel */}
                        <div className="space-y-1">
                            <p className="text-xs font-medium text-gray-700">Min pafyllniva</p>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="text-[10px] text-gray-600">Fra</label>
                                    <input
                                        type="number"
                                        value={filterState.replenishMinLevelFrom}
                                        onChange={(e) => handleNumberRangeChange('replenishMinLevelFrom', parseFloat(e.target.value) || 0)}
                                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="text-[10px] text-gray-600">Til</label>
                                    <input
                                        type="number"
                                        value={filterState.replenishMinLevelTo}
                                        onChange={(e) => handleNumberRangeChange('replenishMinLevelTo', parseFloat(e.target.value) || 9999)}
                                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* ReplenishMaxLevel */}
                        <div className="space-y-1">
                            <p className="text-xs font-medium text-gray-700">Maks pafyllniva</p>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="text-[10px] text-gray-600">Fra</label>
                                    <input
                                        type="number"
                                        value={filterState.replenishMaxLevelFrom}
                                        onChange={(e) => handleNumberRangeChange('replenishMaxLevelFrom', parseFloat(e.target.value) || 0)}
                                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="text-[10px] text-gray-600">Til</label>
                                    <input
                                        type="number"
                                        value={filterState.replenishMaxLevelTo}
                                        onChange={(e) => handleNumberRangeChange('replenishMaxLevelTo', parseFloat(e.target.value) || 9999)}
                                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* CustomSortID */}
                        <div className="space-y-1">
                            <p className="text-xs font-medium text-gray-700">Sortering ID</p>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="text-[10px] text-gray-600">Fra</label>
                                    <input
                                        type="number"
                                        value={filterState.customSortIDFrom}
                                        onChange={(e) => handleNumberRangeChange('customSortIDFrom', parseInt(e.target.value) || 0)}
                                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="text-[10px] text-gray-600">Til</label>
                                    <input
                                        type="number"
                                        value={filterState.customSortIDTo}
                                        onChange={(e) => handleNumberRangeChange('customSortIDTo', parseInt(e.target.value) || 9999)}
                                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* SharedBinMaxQuantity */}
                        <div className="space-y-1">
                            <p className="text-xs font-medium text-gray-700">Maks antall delt</p>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="text-[10px] text-gray-600">Fra</label>
                                    <input
                                        type="number"
                                        value={filterState.sharedBinMaxQuantityFrom}
                                        onChange={(e) => handleNumberRangeChange('sharedBinMaxQuantityFrom', parseInt(e.target.value) || 0)}
                                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="text-[10px] text-gray-600">Til</label>
                                    <input
                                        type="number"
                                        value={filterState.sharedBinMaxQuantityTo}
                                        onChange={(e) => handleNumberRangeChange('sharedBinMaxQuantityTo', parseInt(e.target.value) || 9999)}
                                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
